import React, { useState, useEffect } from 'react';
import {
  Layout, Card, Upload, Button, Table, Input, Form, message, Progress, Tag,
  Space, Tabs, Row, Col, Statistic, Modal, Select, Popconfirm
} from 'antd';
import {
  UploadOutlined, SendOutlined, PhoneOutlined, UserOutlined, MessageOutlined,
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  DeleteOutlined, EditOutlined
} from '@ant-design/icons';
import {
  getContacts, createContact, updateContact, deleteContact
} from '../components/repository/contactApi';
import {
  getCampaigns, createCampaign, updateCampaign, deleteCampaign
} from '../components/repository/campaignApi';
import { bulkUpload } from '../components/repository/bulkUploadApi';

const { Header, Content } = Layout;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

const WhatsAppMarketingTool = () => {
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('1');
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [form] = Form.useForm();
  const [contactForm] = Form.useForm();

  const fetchContacts = async () => {
    setContactsLoading(true);
    try {
      const response = await getContacts();
      setContacts(response.results || response.data || response || []);
    } catch (error) {
      message.error('Failed to fetch contacts');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const response = await getCampaigns();
      setCampaigns(response.results || response.data || response || []);
    } catch (error) {
      message.error('Failed to fetch campaigns');
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchCampaigns();
  }, [activeTab]);

  const handleDelete = async (id) => {
    try {
      await deleteContact(id);
      message.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      message.error('Failed to delete contact');
    }
  };

  const contactColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Space><UserOutlined />{text}</Space>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => <Space><PhoneOutlined />{text}</Space>,
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      render: (group) => <Tag color="blue">{group}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingContact(record);
              setContactModalVisible(true);
              contactForm.setFieldsValue({
                name: record.name,
                phone: record.phone,
                group: record.group,
                status: record.status,
              });
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete Contact"
            description="Are you sure you want to delete this contact?"
            okText="Delete"
            cancelText="Cancel"
            okType="danger"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const campaignColumns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Message Type',
      dataIndex: 'message_type',
      key: 'message_type',
      render: (type) => (
        <Tag color={
          type === 'template' ? 'blue' :
          type === 'text' ? 'green' :
          'purple'
        }>
          {type?.charAt(0).toUpperCase() + type?.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Template',
      dataIndex: 'template_name',
      key: 'template_name',
      render: (val) => val || 'N/A',
    },
    {
      title: 'Total Contacts',
      dataIndex: 'contacts_count',
      key: 'contacts_count',
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          draft: { color: 'gray', icon: null },
          scheduled: { color: 'orange', icon: <ClockCircleOutlined /> },
          in_progress: { color: 'blue', icon: <ClockCircleOutlined /> },
          completed: { color: 'green', icon: <CheckCircleOutlined /> },
          failed: { color: 'red', icon: <ExclamationCircleOutlined /> },
        };
        const cfg = statusConfig[status] || { color: 'default', icon: null };
        return <Tag color={cfg.color}>{cfg.icon}{status?.replace('_', ' ').toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Scheduled At',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      render: (date) => date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Messages/Min',
      dataIndex: 'messages_per_minute',
      key: 'messages_per_minute',
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setCurrentCampaign(record);
              setCampaignModalVisible(true);
              form.setFieldsValue({
                name: record.name,
                message: record.message,
                message_type: record.message_type,
                template_name: record.template_name,
                template_language: record.template_language,
                media_type: record.media_type,
                status: record.status,
                messages_per_minute: record.messages_per_minute,
                scheduled_at: record.scheduled_at ? record.scheduled_at.slice(0, 16) : null,
              });
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete Campaign"
            description="Are you sure you want to delete this campaign?"
            okText="Delete"
            cancelText="Cancel"
            okType="danger"
            onConfirm={async () => {
              try {
                await deleteCampaign(record.id);
                message.success('Campaign deleted');
                fetchCampaigns();
              } catch (error) {
                message.error('Failed to delete campaign');
              }
            }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedContacts,
    onChange: (newSelected) => setSelectedContacts(newSelected),
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      const data = await bulkUpload(formData);
      message.success(`CSV uploaded! ${data.created_count} created, ${data.error_count} errors.`);
      fetchContacts();
    } catch {
      message.error('Error uploading file');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      message.error('Please enter a message');
      return;
    }
    if (selectedContacts.length === 0) {
      message.error('Please select contacts to send messages');
      return;
    }
    setSending(true);
    setSendProgress(0);
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setSendProgress(i);
    }
    const newCampaign = {
      id: crypto.randomUUID(),
      name: `Campaign ${campaigns.length + 1}`,
      message: messageText,
      message_type: 'text',
      template_name: null,
      template_language: 'en',
      media_file: null,
      media_type: null,
      contacts_count: selectedContacts.length,
      status: 'completed',
      scheduled_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      messages_per_minute: 20,
    };
    try {
      await createCampaign(newCampaign);
      message.success(`Messages sent successfully to ${selectedContacts.length} contacts!`);
      fetchCampaigns();
      setMessageText('');
      setSelectedContacts([]);
    } catch {
      message.error('Failed to send messages');
    }
    setSending(false);
    setSendProgress(0);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#25D366', padding: '0 24px' }}>
        <Row align="middle" style={{ height: '100%' }}>
          <Col>
            <Space>
              <MessageOutlined style={{ fontSize: 24, color: 'white' }} />
              <h2 style={{ color: 'white', margin: 0 }}>WhatsApp Marketing Tool</h2>
            </Space>
          </Col>
        </Row>
      </Header>
      <Content style={{ padding: 24 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Dashboard */}
          <TabPane tab="Dashboard" key="1">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Contacts"
                    value={contacts.length}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Campaigns"
                    value={campaigns.length}
                    prefix={<MessageOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Messages Sent"
                    value={campaigns.reduce((sum, c) => sum + (c.contacts_count || 0), 0)}
                    prefix={<SendOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Success Rate"
                    value={
                      campaigns.length > 0 ? Math.round(
                        (campaigns.reduce((sum, c) => sum + (c.delivered_count || 0), 0) /
                          Math.max(campaigns.reduce((sum, c) => sum + (c.contacts_count || 0), 0), 1)
                        ) * 100
                      ) : 0
                    }
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>
            <Card title="Recent Campaigns" style={{ marginBottom: 24 }}>
              <Table
                columns={campaignColumns}
                dataSource={campaigns}
                loading={campaignsLoading}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 1200 }}
                rowKey="id"
              />
            </Card>
          </TabPane>

          {/* Contacts */}
          <TabPane tab="Contacts" key="2">
            <Card title="Contact Management">
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Upload
                    accept=".csv"
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading} size="large">
                      {uploading ? 'Uploading...' : 'Upload CSV File'}
                    </Button>
                  </Upload>
                </Col>
                <Col span={12}>
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    onClick={() => {
                      setEditingContact(null);
                      setContactModalVisible(true);
                      contactForm.resetFields();
                    }}
                  >
                    Add Contact Manually
                  </Button>
                </Col>
              </Row>
              <Table
                columns={contactColumns}
                dataSource={contacts}
                loading={contactsLoading}
                pagination={{ pageSize: 10 }}
                rowSelection={rowSelection}
                rowKey="id"
              />
            </Card>
          </TabPane>

          {/* Send Messages */}
          <TabPane tab="Send Messages" key="3">
            <Row gutter={16}>
              <Col span={16}>
                <Card title="Compose Message">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select placeholder="Choose a message template" style={{ width: '100%' }}>
                      <Option value="welcome">Welcome Message</Option>
                      <Option value="promotion">Promotional Message</Option>
                      <Option value="reminder">Reminder Message</Option>
                      <Option value="custom">Custom Message</Option>
                    </Select>
                    <TextArea
                      rows={6}
                      placeholder="Enter your message here..."
                      value={messageText}
                      onChange={e => setMessageText(e.target.value)}
                      maxLength={1000}
                      showCount
                    />
                    <Space>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        loading={sending}
                        disabled={selectedContacts.length === 0}
                        size="large"
                      >
                        Send to {selectedContacts.length} Contacts
                      </Button>
                      <Button onClick={() => setMessageText('')}>Clear</Button>
                    </Space>
                    {sending && (
                      <Progress
                        percent={sendProgress}
                        status={sendProgress === 100 ? 'success' : 'active'}
                        strokeColor="#25D366"
                      />
                    )}
                  </Space>
                </Card>
              </Col>
              <Col span={8}>
                <Card title="Selected Contacts" style={{ marginBottom: 16 }}>
                  <p>{selectedContacts.length} contacts selected</p>
                  {selectedContacts.length > 0 && (
                    <Button size="small" onClick={() => setSelectedContacts([])}>Clear Selection</Button>
                  )}
                </Card>
                <Card title="Message Preview">
                  <div style={{ background: '#f0f0f0', padding: 12, borderRadius: 8, minHeight: 100 }}>
                    {messageText || 'Your message will appear here...'}
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* Campaign History */}
          <TabPane tab="Campaigns" key="4">
            <Card
              title="Campaign History"
              extra={
                <Button type="primary" onClick={() => {
                  setCurrentCampaign(null);
                  setCampaignModalVisible(true);
                  form.resetFields();
                }}
                >
                  New Campaign
                </Button>
              }
            >
              <Table
                columns={campaignColumns}
                dataSource={campaigns}
                loading={campaignsLoading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
                rowKey="id"
              />
            </Card>
          </TabPane>
        </Tabs>

        {/* Campaign Modal */}
        <Modal
          title={currentCampaign ? 'Edit Campaign' : 'Create Campaign'}
          open={campaignModalVisible}
          onCancel={() => {
            setCampaignModalVisible(false);
            setCurrentCampaign(null);
            form.resetFields();
          }}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              try {
                if (values.scheduled_at && typeof values.scheduled_at !== 'string') {
                  values.scheduled_at = new Date(values.scheduled_at).toISOString();
                }
                if (currentCampaign) {
                  await updateCampaign(currentCampaign.id, values);
                  message.success('Campaign updated successfully');
                } else {
                  await createCampaign(values);
                  message.success('Campaign created successfully');
                }
                fetchCampaigns();
                setCampaignModalVisible(false);
                setCurrentCampaign(null);
                form.resetFields();
              } catch {
                message.error('Failed to save campaign');
              }
            }}
          >
            <Form.Item name="name" label="Campaign Name" rules={[{ required: true, message: 'Please enter campaign name' }]}>
              <Input placeholder="Enter campaign name" />
            </Form.Item>
            <Form.Item name="message" label="Message" rules={[{ required: true, message: 'Please enter message' }]}>
              <TextArea rows={4} placeholder="Enter your message" />
            </Form.Item>
            <Form.Item name="message_type" label="Message Type" initialValue="text" rules={[{ required: true }]}>
              <Select>
                <Option value="text">Text Message</Option>
                <Option value="template">Template Message</Option>
                <Option value="media">Media Message</Option>
              </Select>
            </Form.Item>
            <Form.Item name="template_name" label="Template Name">
              <Input placeholder="Enter template name (optional)" />
            </Form.Item>
            <Form.Item name="template_language" label="Template Language" initialValue="en">
              <Select>
                <Option value="en">English</Option>
                <Option value="es">Spanish</Option>
                <Option value="fr">French</Option>
              </Select>
            </Form.Item>
            <Form.Item name="media_type" label="Media Type">
              <Select>
                <Option value="image">Image</Option>
                <Option value="video">Video</Option>
                <Option value="document">Document</Option>
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="draft" rules={[{ required: true }]}>
              <Select>
                <Option value="draft">Draft</Option>
                <Option value="scheduled">Scheduled</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Form.Item>
            <Form.Item name="messages_per_minute" label="Messages Per Minute" initialValue={20} rules={[{ required: true, type: 'number', min: 1 }]}>
              <Input type="number" placeholder="Enter messages per minute" />
            </Form.Item>
            <Form.Item name="scheduled_at" label="Schedule At">
              <Input type="datetime-local" />
            </Form.Item>
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" htmlType="submit">
                {currentCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
              <Button onClick={() => {
                setCampaignModalVisible(false);
                setCurrentCampaign(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form>
        </Modal>

        {/* Contact Modal */}
        <Modal
          title={editingContact ? 'Edit Contact' : 'Add Contact'}
          open={contactModalVisible}
          onCancel={() => {
            setContactModalVisible(false);
            setEditingContact(null);
            contactForm.resetFields();
          }}
          footer={null}
          destroyOnClose
        >
          <Form
            form={contactForm}
            layout="vertical"
            onFinish={async (values) => {
              try {
                if (editingContact) {
                  await updateContact(editingContact.id, values);
                  message.success('Contact updated');
                } else {
                  await createContact(values);
                  message.success('Contact added');
                }
                setContactModalVisible(false);
                fetchContacts();
                contactForm.resetFields();
                setEditingContact(null);
              } catch {
                message.error('Failed to save contact');
              }
            }}
          >
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="Phone Number" rules={[{ required: true, message: 'Please enter phone number' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="group" label="Group" initialValue="General">
              <Select>
                <Option value="Customers">Customers</Option>
                <Option value="Leads">Leads</Option>
                <Option value="Partners">Partners</Option>
                <Option value="General">General</Option>
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="active" rules={[{ required: true }]}>
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
                <Option value="blocked">Blocked</Option>
              </Select>
            </Form.Item>
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" htmlType="submit">
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </Button>
              <Button onClick={() => {
                setContactModalVisible(false);
                setEditingContact(null);
                contactForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default WhatsAppMarketingTool;
